import { Box, Grid2, useMediaQuery, useTheme } from "@mui/material";
import Section from "pages/components/Section";
import { PaymentPlan } from "Domain/PaymentPlan/Entity/PaymentPlan";
import { formatDateTime, formatPrice } from "pages/Utils/Formats";
import ContractSignature from "pages/components/Molecules/ContractSignature";
import FileOpenIcon from "@mui/icons-material/FileOpen";
import { useState } from "react";
import { useEventListener } from "pages/Hooks/useEventListener";
import { getApiUrl } from "Infrastructure/WebClient";
import {
  PAYMENT_PLAN_CONTRACT,
  PAYMENT_PLAN_CONTRACT_PUBLIC,
} from "Application/RequestTypes";
import PaymentPlanStatusChip from "pages/components/Status/PaymentPlanStatusChip";
import { EXPAND_PAYMENT_PLAN_CONTRACT } from "pages/constants/EventKeys";
import {
  signPaymentPlanContract,
  signPaymentPlanContractPublic,
} from "Application/PaymentPlan/PaymentPlanService";
import OutlinedFieldDisplay from "pages/components/OutlinedFIeldDisplay";

type DisplayPaymentPlanProps = {
  paymentPlan: PaymentPlan;
  refreshPaymentPlan: Function;
  publicToken?: string;
};

type Field = string | { field: string; formatter: Function };

export default function DisplayPaymentPlan({
  paymentPlan,
  refreshPaymentPlan,
  publicToken,
}: DisplayPaymentPlanProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const fields: Array<Field> = [
    {
      field: "financedAmount",
      formatter: formatPrice,
    },
    {
      field: "dueAmount",
      formatter: formatPrice,
    },
    "secured",
    // "numberOfSessions",
    "quote",
    "contractSigned",
    {
      field: "createdAt",
      formatter: formatDateTime,
    },
    {
      field: "updatedAt",
      formatter: formatDateTime,
    },
    "paymentServiceIdentifier",
  ];

  if (paymentPlan.paymentServiceIdentifier) {
    fields.push("paymentServiceIdentifier");
  }

  function PaymentPlanField({ field }: { field: Field }) {
    const value =
      typeof field === "string"
        ? paymentPlan[field as keyof PaymentPlan]
        : field.formatter(paymentPlan[field.field as keyof PaymentPlan]);
    const key = typeof field === "string" ? field : field.field;
    return <OutlinedFieldDisplay field={key} value={value} />;
  }

  const nbRow = 6;
  return (
    <Section
      title={"payment_plan"}
      icon={<PaymentPlanStatusChip status={paymentPlan.status} />}
    >
      <Box
        display={"grid"}
        gap={2}
        width={"100%"}
        gridTemplateColumns={isMobile ? "1fr" : "1fr 1fr"}
      >
        <Box display={"grid"} gap={2} width={"100%"}>
          {fields.slice(0, nbRow).map((field: Field, i: number) => (
            <PaymentPlanField key={i} field={field} />
          ))}
        </Box>
        <Box
          display={"grid"}
          {...(!isMobile && {
            gridTemplateRows: `repeat(${nbRow}, 1fr)`,
          })}
          gap={2}
          width={"100%"}
        >
          {fields.slice(nbRow, -1).map((field: Field, i: number) => (
            <PaymentPlanField key={i} field={field} />
          ))}
          <Box
            {...(!isMobile && {
              sx: {
                alignSelf: "end",
                gridRowStart: "span 3",
                gridRowEnd: -1,
              },
            })}
          >
            <Contract
              paymentPlan={paymentPlan}
              refreshPaymentPlan={refreshPaymentPlan}
              publicToken={publicToken}
            />
          </Box>
        </Box>
      </Box>
    </Section>
  );
}

function Contract({
  paymentPlan,
  refreshPaymentPlan,
  publicToken,
}: DisplayPaymentPlanProps) {
  const [contractModalOpen, setContractModalOpen] = useState(false);

  useEventListener(EXPAND_PAYMENT_PLAN_CONTRACT, () => {
    setContractModalOpen(true);
  });

  function handleContractButton() {
    setContractModalOpen(true);
  }

  function onSignatureSave(e: any) {
    const paymentPlanContractSigner = publicToken
      ? signPaymentPlanContractPublic
      : signPaymentPlanContract;
    const paymentPlanContractParam = publicToken
      ? publicToken
      : paymentPlan.publicId;
    paymentPlanContractSigner(paymentPlanContractParam, e.target.value).then(
      (res: any) => {
        if (!("error" in res)) {
          refreshPaymentPlan(res);
        }
      }
    );
  }
  const contractUrl = publicToken
    ? getApiUrl(PAYMENT_PLAN_CONTRACT_PUBLIC, {
        token: publicToken,
      })
    : getApiUrl(PAYMENT_PLAN_CONTRACT, {
        id: paymentPlan.publicId,
      });
  return (
    <>
      <Grid2
        container
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
        onClick={handleContractButton}
      >
        <OutlinedFieldDisplay
          field={"contract"}
          value={
            <ContractIcon
              name="expand-contract"
              isError={!paymentPlan.contractSigned}
            />
          }
        />
      </Grid2>
      <ContractSignature
        name="contract"
        onSignatureSave={onSignatureSave}
        tradKey="payment-plan"
        contractUrl={contractUrl}
        open={contractModalOpen}
        isSigned={paymentPlan.contractSigned}
        setOpen={setContractModalOpen}
      />
    </>
  );
}

function ContractIcon({ isError, ...props }: any) {
  const color = isError ? "red" : "black";
  return (
    <FileOpenIcon
      sx={{
        marginTop: 1,
        backgroundColor: "white",
        width: "auto",
        height: "3.45em",
        aspectRatio: "none",
        cursor: "pointer",
        color: color,
        border: "2px solid " + color,
        borderRadius: "10px",
        "&:hover": {
          color: "blue",
          borderColor: "blue",
        },
        "&:focus": {
          color: "blue",
          borderColor: "blue",
        },
      }}
      fontSize="large"
      {...props}
    />
  );
}
